import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import ARCameraView from "@/components/ARCameraView";
import { Loader2, AlertCircle, Sparkles, ArrowDownToLine } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ARMobileView() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAR, setShowAR] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/projects/${id}`);
        if (!cancelled) setProject(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Projekt nicht gefunden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#05050A] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#00F0FF] animate-spin" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="fixed inset-0 bg-[#05050A] flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <AlertCircle className="w-12 h-12 text-[#FF3B30] mx-auto mb-4" />
          <h2 className="font-['Outfit'] text-xl text-white mb-2">Projekt nicht verfügbar</h2>
          <p className="text-[#A0A0B0] text-sm">{error || "Bitte prüfen Sie den Link oder fragen Sie einen aktuellen QR-Code an."}</p>
        </div>
      </div>
    );
  }

  const visualElements = project.analysis?.visualElements || [];
  const screens = visualElements.filter((e) => e.type === "Plane");
  const funcs = visualElements.filter((e) => e.type === "Cube");

  return (
    <div data-testid="ar-mobile-view" className="fixed inset-0 bg-[#05050A] overflow-y-auto">
      {/* Hero Card */}
      <div className="relative px-5 pt-8 pb-6 bg-gradient-to-b from-[#00F0FF]/10 via-[#05050A] to-[#05050A]">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-[#00F0FF]" />
          <span className="text-[10px] font-['JetBrains_Mono'] text-[#00F0FF] tracking-[0.25em] uppercase">Project Radar AR</span>
        </div>
        <h1 className="font-['Outfit'] text-2xl font-bold text-white leading-tight mb-2">
          {project.projectTitle}
        </h1>
        <p className="text-xs text-[#A0A0B0] mb-5">{project.projectCategory}</p>

        <div className="grid grid-cols-3 gap-2">
          <div className="glass-surface rounded-lg p-3 border border-white/5">
            <div className="text-[9px] font-['JetBrains_Mono'] text-[#606070] uppercase tracking-wider mb-1">Screens</div>
            <div className="text-xl font-['JetBrains_Mono'] font-bold text-[#00F0FF]">{screens.length}</div>
          </div>
          <div className="glass-surface rounded-lg p-3 border border-white/5">
            <div className="text-[9px] font-['JetBrains_Mono'] text-[#606070] uppercase tracking-wider mb-1">Backend</div>
            <div className="text-xl font-['JetBrains_Mono'] font-bold text-[#FFB800]">{funcs.length}</div>
          </div>
          <div className="glass-surface rounded-lg p-3 border border-white/5">
            <div className="text-[9px] font-['JetBrains_Mono'] text-[#606070] uppercase tracking-wider mb-1">Komplex.</div>
            <div className="text-sm font-['Outfit'] font-bold text-white pt-1">{project.analysis?.projectComplexity || "—"}</div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="px-5 py-6">
        <button
          data-testid="ar-mobile-launch-btn"
          onClick={() => setShowAR(true)}
          disabled={!visualElements.length}
          className="w-full py-5 rounded-2xl bg-gradient-to-r from-[#00F0FF] to-[#00B5C5] text-[#05050A] font-['Outfit'] font-bold text-base shadow-[0_0_40px_rgba(0,240,255,0.4)] active:scale-[0.98] transition-transform disabled:opacity-40 disabled:shadow-none"
        >
          AR-Ansicht starten
        </button>
        <p className="text-[11px] text-[#606070] text-center mt-3 leading-relaxed">
          Erlauben Sie den Kamerazugriff. Die 3D-Architektur des Projekts erscheint dann <span className="text-[#00F0FF]">vor Ihren Augen im Raum</span>.
        </p>
      </div>

      {/* Component list */}
      <div className="px-5 pb-8">
        <div className="text-[10px] font-['JetBrains_Mono'] text-[#606070] uppercase tracking-wider mb-3 flex items-center gap-2">
          <ArrowDownToLine className="w-3 h-3" /> Was im Projekt steckt
        </div>
        <div className="space-y-2">
          {screens.map((el, i) => (
            <div key={`s${i}`} className="px-3 py-2.5 rounded-lg bg-[#00F0FF]/[0.04] border border-[#00F0FF]/15">
              <div className="flex items-start gap-2">
                <span className="w-5 h-5 flex items-center justify-center rounded bg-[#00F0FF]/15 text-[10px] font-['JetBrains_Mono'] text-[#00F0FF] font-bold flex-shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-white font-semibold">{el.name}</div>
                  {el.description && <p className="text-[10px] text-[#808090] mt-0.5 leading-relaxed">{el.description}</p>}
                </div>
              </div>
            </div>
          ))}
          {funcs.map((el, i) => (
            <div key={`f${i}`} className="px-3 py-2.5 rounded-lg bg-[#FFB800]/[0.04] border border-[#FFB800]/15">
              <div className="flex items-start gap-2">
                <span className="w-5 h-5 flex items-center justify-center rounded bg-[#FFB800]/15 text-[10px] font-['JetBrains_Mono'] text-[#FFB800] font-bold flex-shrink-0">{screens.length + i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-white font-semibold">{el.name}</div>
                  {el.description && <p className="text-[10px] text-[#808090] mt-0.5 leading-relaxed">{el.description}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="px-5 py-4 border-t border-white/5 text-center">
        <p className="text-[10px] text-[#606070]">Powered by <span className="text-[#00F0FF]">Project Radar AR</span></p>
      </div>

      <ARCameraView isOpen={showAR} onClose={() => setShowAR(false)} visualElements={visualElements} />
    </div>
  );
}
